import { CLIENT_CONTRACT } from "./client_contract"

export const WEB_SETTINGS_KEY = "gr8tracks:map-prefs"

export const WEB_SETTING_KEYS = {
  locationIntervalSec: "locationIntervalSec",
  followZoom: "followZoom",
  followPitch: "followPitch",
  followPitch3d: "followPitch3d",
  autoRecenterSec: "autoRecenterSec",
  streetNames: "streetNames",
  defaultSelfTrack: "defaultSelfTrack",
  autoTrackFriends: "autoTrackFriends"
}

export const WEB_DEFAULT_SETTINGS = Object.freeze({ ...CLIENT_CONTRACT.web.settings.defaults })

export function readStoredMapPrefs() {
  try { return JSON.parse(localStorage.getItem(WEB_SETTINGS_KEY) || "{}") || {} } catch { return {} }
}

export function writeStoredMapPrefs(prefs) {
  try { localStorage.setItem(WEB_SETTINGS_KEY, JSON.stringify(prefs || {})) } catch { /* storage off */ }
}

export function readWebSettings() {
  const prefs = readStoredMapPrefs()
  return mapPrefsLoadState(prefs).settings
}

export function readWebSetting(key) {
  return readWebSettings()[key]
}

export function writeWebSetting(key, value) {
  const prefs = readStoredMapPrefs()
  prefs.settings = { ...WEB_DEFAULT_SETTINGS, ...(prefs.settings || {}), [key]: value }
  writeStoredMapPrefs(prefs)
}

export function coerceWebSetting(key, value, checked = false) {
  if ([WEB_SETTING_KEYS.streetNames, WEB_SETTING_KEYS.defaultSelfTrack, WEB_SETTING_KEYS.autoTrackFriends].includes(key)) return !!checked
  const n = Number(value)
  return Number.isFinite(n) ? n : WEB_DEFAULT_SETTINGS[key]
}

export function webSettingControlState(settings = {}, control = {}) {
  const merged = { ...WEB_DEFAULT_SETTINGS, ...(settings || {}) }
  const key = control.key
  if (!key || !(key in merged)) return { applies: false }

  const value = merged[key]
  if (control.type === "checkbox") return { applies: true, checked: !!value }

  return {
    applies: true,
    value: control.tagName === "SELECT" ? nearestSelectValue(value, control.optionValues) : String(value)
  }
}

export function mapPrefsLoadState(prefs = {}) {
  return {
    settings: { ...WEB_DEFAULT_SETTINGS, ...(prefs.settings || {}) },
    layers: prefs.layers || {},
    uses: prefs.uses || {},
    auto: typeof prefs.auto === "boolean" ? prefs.auto : null,
    basemap: loadBasemapMode(prefs)
  }
}

export function buildStoredMapPrefs(existingPrefs = {}, current = {}) {
  return {
    ...existingPrefs,
    layers: checksByKey(current.layerControls, "layer"),
    uses: checksByKey(current.useControls, "use"),
    auto: typeof current.autoChecked === "boolean" ? current.autoChecked : null,
    basemap: current.basemapMode || "streets",
    settings: { ...WEB_DEFAULT_SETTINGS, ...(existingPrefs.settings || {}), ...(current.settings || {}) }
  }
}

function loadBasemapMode(prefs = {}) {
  if (typeof prefs.basemap === "string") return prefs.basemap
  if (typeof prefs.satellite === "boolean") return prefs.satellite ? "satellite" : "streets"
  return null
}

function checksByKey(controls = [], datasetKey) {
  return Array.from(controls || {}).reduce((checks, control) => {
    const key = control?.dataset?.[datasetKey]
    if (key) checks[key] = !!control.checked
    return checks
  }, {})
}

function nearestSelectValue(value, optionValues = []) {
  const raw = String(value)
  const values = Array.from(optionValues || []).map(String)
  if (values.includes(raw) || !Number.isFinite(Number(value))) return raw

  const numericOptions = values
    .map(optionValue => ({ optionValue, number: Number(optionValue) }))
    .filter(option => Number.isFinite(option.number))
  if (!numericOptions.length) return raw

  return numericOptions.reduce((best, option) =>
    Math.abs(option.number - value) < Math.abs(best.number - value) ? option : best
  ).optionValue
}
